/**
 * Admissions CRM — Refresh-sync API contract (plan #001, U6).
 *
 * POST /v1/enrollment/sync/refresh — "Refresh now".
 *
 * Staff-triggered manual run of the ENROLLMENTS chassis sync (R5, KTD9). The
 * route is the chassis job, NOT a CRM-local path: it shares the one-in-flight
 * lock with the autopilot scheduler, refuses to chain back-to-back fan-outs
 * (cooldown), and refuses to burn a ~1,150-call fan-out on a dead/absent token.
 *
 * The web POSTs this, then polls the enrollment STATE endpoint until the run
 * completes (the state endpoint exposes the running flag), then invalidates its
 * queries so the table refetches from the mirror — never from Blackbaud live.
 *
 * The response is a DISCRIMINATED state so the web can branch:
 *   - `enqueued`     — a new job was created (carries the job's publicId).
 *   - `in_progress`  — a job is already running and not stuck; no second job
 *                      created. The web keeps polling.
 *   - `too_soon`     — within the cooldown window of the last COMPLETED run
 *                      (KTD9). DISTINCT from `in_progress`: the in-flight lock
 *                      alone lets an admin chain back-to-back runs, so the
 *                      cooldown is the real guard. Carries `retryAfterSeconds`.
 *   - `needs_reauth` — the school's data-lookup token is absent/dead; enqueue is
 *                      SKIPPED rather than spending the fan-out on a dead token.
 *
 * Admin-only, school-scoped — the school is the active-school request context,
 * never a caller-supplied param (R14).
 */

import { z } from 'zod'

/** The four distinct outcomes of a refresh request (discriminator = `status`). */
export const EnrollmentRefreshStatusSchema = z.enum(['enqueued', 'in_progress', 'too_soon', 'needs_reauth'])
export type EnrollmentRefreshStatus = z.infer<typeof EnrollmentRefreshStatusSchema>

/** A new chassis job was created and is running ASYNC in the API task. */
export const EnrollmentRefreshEnqueuedSchema = z.object({
  status: z.literal('enqueued'),
  /** PublicId of the created `PlatformDataSyncJob`, when the chassis returns it. */
  jobPublicId: z.string().nullable()
})

/** A job is already in flight (and not stuck) — no second job was created. */
export const EnrollmentRefreshInProgressSchema = z.object({
  status: z.literal('in_progress'),
  /** Whole minutes the in-flight job has been running, when known. */
  minutesRunning: z.number().int().nullable()
})

/** Within the cooldown window of the last completed run — refresh refused. */
export const EnrollmentRefreshTooSoonSchema = z.object({
  status: z.literal('too_soon'),
  /** Seconds the caller should wait before retrying (cooldown remainder). */
  retryAfterSeconds: z.number().int().nonnegative(),
  /** The last completed-run timestamp the cooldown is measured from (ISO 8601). */
  lastCompletedAt: z.string().nullable()
})

/** The school's data-lookup token is absent/dead — enqueue skipped. */
export const EnrollmentRefreshNeedsReauthSchema = z.object({
  status: z.literal('needs_reauth')
})

export const RefreshEnrollmentSyncResponseSchema = z.discriminatedUnion('status', [
  EnrollmentRefreshEnqueuedSchema,
  EnrollmentRefreshInProgressSchema,
  EnrollmentRefreshTooSoonSchema,
  EnrollmentRefreshNeedsReauthSchema
])
export type RefreshEnrollmentSyncResponse = z.infer<typeof RefreshEnrollmentSyncResponseSchema>
